import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Col, Modal, Row, Spin, Switch } from 'antd';

import CustomMaskedInput from 'components/MaskedInput/index';
import { Wysiwyg } from 'v2/components/Shared/Wysiwyg';
import { useCreateRecoveryMessage } from 'v2/hooks/recoveryMessages/useCreateRecoveryMessage';
import { useUpdateRecoveryMessage } from 'v2/hooks/recoveryMessages/useUpdateRecoveryMessage';

export interface IRecoveryMessage {
  active: boolean;
  days: string;
  description: string;
  id?: number;
  text: string;
  title: string;
}

interface IProps {
  open: boolean;
  handleClose: () => void;
  recoveryEmail: any;
  editing: boolean;
  idProduct: number;
  registeredMessages: IRecoveryMessage[];
}

const DEFAULT_RECOVERY_MESSAGE: IRecoveryMessage = {
  active: true,
  days: '1',
  description: '',
  text: '',
  title: '',
  id: null
};

const EMPTY_HTML_PATTERNS = ['<p></p>', '<p><br></p>'];

const RecoveryMessageForm = ({ open, handleClose, recoveryEmail, editing, idProduct, registeredMessages }: IProps) => {
  const [recoveryMessage, setRecoveryMessage] = useState<IRecoveryMessage>({ ...DEFAULT_RECOVERY_MESSAGE });

  const updateMutation = useUpdateRecoveryMessage();
  const createMutation = useCreateRecoveryMessage();
  const submitting = updateMutation.isPending || createMutation.isPending;

  useEffect(() => {
    if (editing && recoveryEmail) {
      setRecoveryMessage({ ...recoveryEmail });
    }
  }, [editing, recoveryEmail]);

  const handleChange = useCallback((type: string, value: any) => {
    setRecoveryMessage(prev => ({ ...prev, [type]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!recoveryMessage.description) {
      toast.error('A descrição é obrigatória');
      return;
    }

    if (recoveryMessage.days === '') {
      toast.error('Dias é obrigatório');
      return;
    }

    if (Number.parseInt(recoveryMessage.days) < 0) {
      toast.error('Dias não pode ser menor que 0');
      return;
    }

    const hasMessageForThatDay = registeredMessages.some(
      (registeredMessage: IRecoveryMessage) =>
        registeredMessage.days == recoveryMessage.days &&
        registeredMessage.active &&
        registeredMessage.id !== recoveryMessage.id
    );

    if (hasMessageForThatDay) {
      toast.error('Já existe uma mensagem cadastrada para esse dia');
      return;
    }

    if (!recoveryMessage.text?.trim() || EMPTY_HTML_PATTERNS.includes(recoveryMessage.text)) {
      toast.error('Preencha a mensagem para e-mail');
      return;
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({
          productId: idProduct,
          messageId: recoveryMessage.id,
          data: recoveryMessage
        });
        toast.success('Mensagem alterada com sucesso!');
      } else {
        await createMutation.mutateAsync({
          productId: idProduct,
          data: recoveryMessage
        });
        toast.success('Mensagem criada com sucesso!');
      }

      handleClose();
    } catch {
      toast.error('Erro ao salvar mensagem de recuperação');
    }
  }, [recoveryMessage, registeredMessages, editing, updateMutation, createMutation, idProduct, handleClose]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={<span className='text-lg font-semibold text-[#192542]'>Nova mensagem</span>}
      width={640}
      footer={
        <div className='flex justify-end gap-2'>
          <Button disabled={submitting} onClick={handleClose}>
            Fechar
          </Button>
          <Button type='primary' disabled={submitting} onClick={handleSubmit}>
            Salvar
            {submitting && <Spin size='small' className='ml-2' />}
          </Button>
        </div>
      }
    >
      <div>Crie ou edite sua mensagem de recuperação de venda</div>
      <div className='m-4' />
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <div className='flex items-center gap-2'>
            <Switch checked={recoveryMessage.active} onChange={(checked: boolean) => handleChange('active', checked)} />
            <span>Ativo</span>
          </div>
        </Col>
        <Col xs={24} md={20}>
          <label className='mb-1 block text-sm'>Descrição</label>
          <input
            className='w-full rounded-sm border border-gray-300 px-3 py-2'
            value={recoveryMessage.description}
            onChange={(e: any) => handleChange('description', e.target.value)}
          />
        </Col>
        <Col xs={24} md={4}>
          <CustomMaskedInput
            mask='number'
            label='Dias'
            disableUnderline
            value={recoveryMessage.days}
            onChange={(value: any) => handleChange('days', value)}
          />
        </Col>
        <Col span={24}>
          <label className='mb-1 block text-sm'>Título do E-mail</label>
          <input
            className='w-full rounded-sm border border-gray-300 px-3 py-2'
            value={recoveryMessage.title}
            onChange={(e: any) => handleChange('title', e.target.value)}
          />
        </Col>
        <Col span={24}>
          <div>
            <label className='mb-1 block text-sm'>Mensagem do E-mail</label>
            <Wysiwyg value={recoveryMessage.text || ''} onChange={(v: string) => handleChange('text', v)} />
          </div>
        </Col>
        <Col span={24}>
          <span className='text-text-heading text-lg font-semibold'>Nova mensagem</span>
          <Fragment>
            <p>
              <b>[[PRIMEIRONOME]]</b> - Primeiro nome do cliente
            </p>
            <p>
              <b>[[NOMEDOPRODUTOR]]</b> - Nome e sobrenome do produtor
            </p>
            <p>
              <b>[[NOMECONTEUDO]]</b> - Nome do conteúdo em recuperação
            </p>
            <p>
              <b>[[DTFATURA]]</b> - Data de abertura da fatura / Ínicio do processo de compra
            </p>
            <p>
              <b>[[DTFATVENC]]</b> - Data de vencimento da fatura
            </p>
            <p>
              <b>[[FATCOD]]</b> - Código da fatura gerada na Eduzz
            </p>
            <p>
              <b>[[CNTCOD]]</b> - Código do seu produto
            </p>
            <p>
              <b>[[CHAVEGW]]</b> - Usado para geração do link de boleto, ex:
              <span>https://sun.eduzz.com/thankyou/[[CNTCOD]]/[[CHAVEGW]]</span>
            </p>
          </Fragment>
        </Col>
      </Row>
    </Modal>
  );
};

export default RecoveryMessageForm;
